import * as React from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import MuiCard from '@mui/material/Card';
import Checkbox from '@mui/material/Checkbox';
import Divider from '@mui/material/Divider';
import FormLabel from '@mui/material/FormLabel';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import Link from '@mui/material/Link';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { styled } from '@mui/material/styles';
import { GoogleIcon, MicrosoftIcon, SitemarkIcon } from './CustomIcons';

const Card = styled(MuiCard)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignSelf: 'center',
  width: '100%',
  padding: theme.spacing(4),
  gap: theme.spacing(2),
  boxShadow:
    'hsla(220, 30%, 5%, 0.05) 0px 5px 15px 0px, hsla(220, 25%, 10%, 0.05) 0px 15px 35px -5px',
  [theme.breakpoints.up('sm')]: {
    width: '450px',
  },
  ...theme.applyStyles('dark', {
    boxShadow:
      'hsla(220, 30%, 5%, 0.5) 0px 5px 15px 0px, hsla(220, 25%, 10%, 0.08) 0px 15px 35px -5px',
  }),
}));

export default function SignUpCard() {
  const [emailError, setEmailError] = React.useState(false);
  const [emailErrorMessage, setEmailErrorMessage] = React.useState('');
  const [passwordError, setPasswordError] = React.useState(false);
  const [passwordErrorMessage, setPasswordErrorMessage] = React.useState('');
  const [nameError, setNameError] = React.useState(false);
  const [nameErrorMessage, setNameErrorMessage] = React.useState('');
  const [verificationCode, setVerificationCode] = React.useState('');
  const [showVerification, setShowVerification] = React.useState(false);
  const [verificationError, setVerificationError] = React.useState(false);
  const [verificationErrorMessage, setVerificationErrorMessage] = React.useState('');

  const handleVerificationSubmit = async () => {
    if (!verificationCode) {
      setVerificationError(true);
      setVerificationErrorMessage('Please enter verification code');
      return;
    }
    
    try {
      var response = await fetch('https://api.vivachain.ai/v1/register/verify', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          code: verificationCode 
        }),
        credentials: "include"
      });

      if (response.ok) {
        window.location.href = 'https://chat.vivachain.ai';
      } else {
        throw new Error('Verification failed');
      }
    } catch (error) {
      setVerificationError(true);
      setVerificationErrorMessage('Invalid verification code');
    }
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!validateInputs()) return;

    const data = new FormData(event.currentTarget);
    try {
      const response = await fetch('https://api.vivachain.ai/v1/register', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: data.get('email'),
          password: data.get('password'),
          fullName: data.get('name')
        }),
      });

      if (response.ok) {
        setShowVerification(true);
      } else {
        throw new Error('Signup failed');
      }
    } catch (error) {
      console.error('Signup failed:', error);
    }
  };

  React.useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const token = params.get('code');
    if (token) {
      setVerificationCode(token);
      setShowVerification(true);
    }
  }, []);

  const validateInputs = () => {
    const email = document.getElementById('email') as HTMLInputElement;
    const password = document.getElementById('password') as HTMLInputElement;
    const name = document.getElementById('name') as HTMLInputElement;

    let isValid = true;

    if (!name.value) {
      setNameError(true);
      setNameErrorMessage('Please enter your name');
      isValid = false;
    } else {
      setNameError(false);
      setNameErrorMessage('');
    }

    if (!email.value || !/\S+@\S+\.\S+/.test(email.value)) {
      setEmailError(true);
      setEmailErrorMessage('Please enter a valid email address.');
      isValid = false;
    } else {
      setEmailError(false);
      setEmailErrorMessage('');
    }

    if (!password.value || password.value.length < 6) {
      setPasswordError(true);
      setPasswordErrorMessage('Password must be at least 6 characters long.');
      isValid = false;
    } else {
      setPasswordError(false);
      setPasswordErrorMessage('');
    }

    return isValid;
  };

  const handleProviderLogin = (provider: string) => {
    callLoginApi(provider);
  };

  const callLoginApi = async (provider?: string) => {
    try {
      const redirectUrl = encodeURIComponent("https://chat.vivachain.ai");
      window.location.href = `https://api.vivachain.ai/v1/auth/login?provider=${provider}&redirectUrl=${redirectUrl}`;
    } catch (err) {
      console.error('Error logging in:', err);
      alert('An error occurred. Please try again.');
    }
  };

  return (
    <Card variant="outlined">
      <Box sx={{ display: { xs: 'flex', md: 'none' } }}>
        <SitemarkIcon />
      </Box>
      <Typography
        component="h1"
        variant="h4"
        sx={{ width: '100%', fontSize: 'clamp(2rem, 10vw, 2.15rem)' }}
      >
        {showVerification ? 'Verify Email' : 'Sign up'}
      </Typography>

      {!showVerification ? (
        <Box
          component="form"
          onSubmit={handleSubmit}
          noValidate
          sx={{ display: 'flex', flexDirection: 'column', width: '100%', gap: 2 }}
        >
          <FormControl>
            <FormLabel htmlFor="name">Full name</FormLabel>
            <TextField
              autoComplete="name"
              name="name"
              required
              fullWidth
              id="name"
              placeholder="Jon Snow"
              error={nameError}
              helperText={nameErrorMessage}
              color={nameError ? 'error' : 'primary'}
            />
          </FormControl>
          <FormControl>
            <FormLabel htmlFor="email">Email</FormLabel>
            <TextField
              required
              fullWidth
              id="email"
              placeholder="your@email.com"
              name="email"
              autoComplete="email"
              variant="outlined"
              error={emailError}
              helperText={emailErrorMessage}
              color={emailError ? 'error' : 'primary'}
            />
          </FormControl>
          <FormControl>
            <FormLabel htmlFor="password">Password</FormLabel>
            <TextField
              required
              fullWidth
              name="password"
              placeholder="••••••"
              type="password"
              id="password"
              autoComplete="new-password"
              variant="outlined"
              error={passwordError}
              helperText={passwordErrorMessage}
              color={passwordError ? 'error' : 'primary'}
            />
          </FormControl>
          <FormControlLabel
            control={<Checkbox value="allowExtraEmails" color="primary" />}
            label="I want to receive updates via email."
          />
          <Button type="submit" fullWidth variant="contained">
            Sign up
          </Button>
        </Box>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%', gap: 2 }}>
          <Typography variant="body1" sx={{ textAlign: 'center' }}>
            Please enter the verification code sent to your email
          </Typography>
          <FormControl>
            <TextField
              required
              fullWidth
              value={verificationCode}
              onChange={(e) => setVerificationCode(e.target.value)}
              placeholder="Enter verification code"
              error={verificationError}
              helperText={verificationErrorMessage}
              sx={{ textAlign: 'center' }}
            />
          </FormControl>
          <Button 
            fullWidth 
            variant="contained"
            onClick={handleVerificationSubmit}
          >
            Verify
          </Button>
          <Button
            fullWidth
            variant="text"
            onClick={() => setShowVerification(false)}
          >
            Back to Sign up
          </Button>
        </Box>
      )}

      <Divider>or</Divider>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Button
          fullWidth
          variant="outlined"
          onClick={() => handleProviderLogin('google')}
          startIcon={<GoogleIcon />}
        >
          Sign in with Google
        </Button>
        <Typography sx={{ textAlign: 'center' }}>
          Already have an account?{' '}
          <Link
            href="/auth/login"
            variant="body2"
            sx={{ alignSelf: 'center' }}
          >
            Sign in
          </Link>
        </Typography>
      </Box>
    </Card>
  );
}
